import * as React from 'react'
import { cloneDeep, fromPairs } from 'lodash'
import { IFilterContext, IFilterContextProvider, ITooltipFormSubmit, TFilter, TPage } from 'context/interface'
import { SORT_DIRECTION } from 'context/constants'

const { useState } = React

const FilterContext = React.createContext({} as IFilterContextProvider)

const useFilterContext = (): IFilterContextProvider => {
  const [filters, setFilters] = useState<TFilter>({})
  const [baseFilters, setBaseFilters] = useState<TFilter>({})
  const [selectedFilters, setSelectedFilters] = useState<TFilter>({})
  const [selectedPage, setSelectedPage] = useState<TPage>({})

  const setFirstFilters = (filters2: TFilter) => {
    setFilters(cloneDeep(filters2))
  }

  const setFirstBaseFilters = (filters2: TFilter) => {
    setBaseFilters(cloneDeep(filters2))
  }

  const cleanComponentFilters = () => {
    cleanAllFilters('Transfer')
    cleanAllFilters('Events')
    cleanAllFilters('Payments')
    cleanAllFilters('Logs')
    cleanAllFilters('PaymentLink')
    cleanAllFilters('InvoiceDetails')
    destroyFilters()
    setSelectedFilters({})
  }

  const updateObjectFilters = (filter: TFilter, newFilters: ITooltipFormSubmit): void => {
    Object.keys(newFilters).forEach(filter_name => {
      Object.keys(newFilters[filter_name]).forEach(filter_key => {
        if (filter[filter_name] && filter[filter_name].filters) {
          filter[filter_name].filters[filter_key].initialValue = newFilters[filter_name][filter_key]
        }
      })
    })
  }

  const updateFilters = (
    newFilters: ITooltipFormSubmit,
    filterComponentName?: string,
    forcedFilters?: ITooltipFormSubmit
  ): void => {
    const copy = cloneDeep(filters)

    updateObjectFilters(copy, newFilters)

    if (forcedFilters) {
      updateObjectFilters(copy, forcedFilters)
    }
    setFilters(copy)
    setSelectedFilterContext(filterComponentName, copy)
    setSelectedPageContext(filterComponentName, 1)
  }

  const restartFilter = (filterToRestart: TFilter, filterName: string, filterKey: string): void => {
    filterToRestart[filterName].filters[filterKey] = { ...baseFilters[filterName].filters[filterKey] }
  }

  const cleanFilter = (
    filterName: string,
    filterKey: string,
    filterComponentName?: string,
    forcedFilters?: ITooltipFormSubmit
  ): void => {
    setFilters(cleanFilterOnFilters(filters, filterName, filterKey, forcedFilters))

    if (filterComponentName) {
      const selectedFilter = getSelectedFilterContext(filterComponentName)
      const cleanSelectedFilters = cleanFilterOnFilters(selectedFilter, filterName, filterKey, forcedFilters)
      setSelectedFilterContext(filterComponentName, cleanSelectedFilters)
      setSelectedPageContext(filterComponentName, 1)
    }
  }

  const cleanFilterOnFilters = (
    filtersToClean: TFilter,
    filterName: string,
    filterKey: string,
    forcedFilters?: ITooltipFormSubmit
  ): TFilter => {
    const copy = cloneDeep(filtersToClean)

    if (forcedFilters) {
      updateObjectFilters(copy, forcedFilters)
    }

    if (copy[filterName].joinFunction) {
      Object.keys(copy[filterName].filters).forEach(filterKeyToRestart =>
        restartFilter(copy, filterName, filterKeyToRestart)
      )
    } else {
      restartFilter(copy, filterName, filterKey)
    }

    return copy
  }

  const destroyFilters = (): void => {
    setFilters({})
    setBaseFilters({})
  }

  const cleanAllFilters = (filterComponentName?: string): void => {
    const baseFiltersTmp = cloneDeep(baseFilters)
    setFilters(baseFiltersTmp)
    setSelectedFilterContext(filterComponentName, baseFiltersTmp)
    setSelectedPageContext(filterComponentName, 1)
  }

  const getInitialValue = (filtersToSearch: TFilter, filter_name: string, filter_key: string): any => {
    const filter = filtersToSearch[filter_name].filters[filter_key]

    return filter.backFormat ? filter.backFormat() : filter.initialValue
  }

  const getFiltersToSearch = (): Record<string, any> => {
    return fromPairs(
      Object.keys(filters || {}).map(filter_name => {
        const filter = filters[filter_name]

        if (filter.directValue) {
          return [
            filter_name,
            filter.filters.value.backFormat ? filter.filters.value.backFormat() : filter.filters.value.initialValue
          ]
        } else if (filter.backFormat) {
          return [filter_name, filter.backFormat()]
        } else {
          const filter_keys = Object.keys(filter.filters)
          if (filter.joinFunction) {
            return [
              filter_name,
              fromPairs(filter_keys.map(filter_key => [filter_key, getInitialValue(filters, filter_name, filter_key)]))
            ]
          } else {
            return [filter_name, filter_keys.filter(filter_key => getInitialValue(filters, filter_name, filter_key))]
          }
        }
      })
    )
  }

  const changeOrder = (labelName = ''): void => {
    if (labelName === '') {
      return
    }

    const [sortByName, sortDirectionName] = [
      'sortBy' in filters ? 'sortBy' : 'sort_by',
      'sortDirection' in filters ? 'sortDirection' : 'sort_direction'
    ]

    const newSort = { [sortByName]: { value: labelName }, [sortDirectionName]: { value: SORT_DIRECTION.ASC } }

    const sortBy = filters[sortByName].filters.value.initialValue

    if (sortBy === labelName) {
      const sortDirection = filters[sortDirectionName].filters.value.initialValue
      newSort[sortDirectionName].value = sortDirection === SORT_DIRECTION.ASC ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC
    }

    updateFilters(newSort)
  }

  const getSelectedFilterContext = (filterComponentName: string): any => {
    return selectedFilters[filterComponentName]
  }

  const setSelectedFilterContext = (filterComponentName: string | undefined, data: any): void => {
    if (filterComponentName) {
      if (Object.keys(selectedFilters).length !== 0) {
        selectedFilters[filterComponentName] = data
        setSelectedFilters(selectedFilters)
        return
      }

      setSelectedFilters({ [filterComponentName]: data })
    }
  }

  const getSelectedPageContext = (filterComponentName: string): number => {
    return selectedPage[filterComponentName]
  }

  const setSelectedPageContext = (filterComponentName: string | undefined, page: number): void => {
    if (filterComponentName) {
      if (Object.keys(selectedPage).length !== 0) {
        selectedPage[filterComponentName] = page
        setSelectedPage(selectedPage)
        return
      }

      setSelectedPage({ [filterComponentName]: page })
    }
  }

  return {
    filters,
    setFilters: setFirstFilters,
    setBaseFilters: setFirstBaseFilters,
    updateFilters,
    cleanAllFilters,
    destroyFilters,
    cleanFilter,
    getFiltersToSearch,
    changeOrder,
    getSelectedFilterContext,
    setSelectedFilterContext,
    getSelectedPageContext,
    setSelectedPageContext,
    cleanComponentFilters
  }
}

const FilterContextProvider = ({ children }: IFilterContext) => {
  const values = useFilterContext()
  return <FilterContext.Provider value={values}>{children}</FilterContext.Provider>
}

export { FilterContext, FilterContextProvider, useFilterContext }
